import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './index.module.scss';
import { getImageFromS3 } from '../../../../imageService';
import { ReactComponent as BrandMark } from '../brand-mark.svg';
import { ReactComponent as ScrollHint } from '../scroll-hint.svg';
import { ReactComponent as DownloadIcon } from '../download.svg';
import { ReactComponent as ReviewChevronIcon } from '../review-chevron.svg';
import { ReactComponent as CheckIcon } from '../check.svg';

const MAX_SELECTED = 2;

const PILL_REVEAL_OFFSET = 100;

const COLORS = {
  blue: { from: '#2fd2ff', to: '#00498d' },
  green: { from: '#05ff33', to: '#094a00' },
  coffee: { from: '#642c0c', to: '#3d1a05' },
  red: { from: '#ff2f32', to: '#8d0000' },
  yellow: { from: '#fff705', to: '#8e7905' }
};

const WALLPAPER_HINT =
  'Лови персоніфіковану заставку — нехай вона щодня нагадує про твій власний, особливий ритм.';

const ARCHETYPES = [
  {
    id: 'naturalist',
    colors: ['green', 'blue'],
    name: 'Натураліст',
    description:
      'Твоя суперсила — це легкість, амбітність та свіжий погляд на речі. Ти вмієш залишатися у фокусі та не втрачати бачення головного навіть у шаленому темпі мегаполісу.'
  },
  {
    id: 'hedonist',
    colors: ['coffee', 'green'],
    name: 'Гедоніст',
    description:
      'Ти вмієш сповільнювати час і насолоджуватися моментом. Твоє місто — це про душевний затишок, м’які паузи серед дня та вміння жити «тут і зараз».'
  },
  {
    id: 'aesthete',
    colors: ['red', 'green'],
    name: 'Естет',
    description:
      'Ти бачиш красу в деталях, повз які інші просто пробігають. Речі з історією, автентичність та гармонія часу — ось що тебе найбільше надихає.'
  },
  {
    id: 'adventurer',
    colors: ['green', 'yellow'],
    name: 'Ловець пригод',
    description:
      'Ти вмієш помічати радість у дрібницях і несеш цей вайб далі. У тобі живе безтурботність, допитливість і щире захоплення кожним новим днем.'
  },
  {
    id: 'dreamer',
    colors: ['coffee', 'blue'],
    name: 'Мрійник',
    description:
      'Внутрішній спокій та глибока гармонія — твої головні орієнтири. Ти відновлюєш ресурс, спостерігаючи за плинністю речей і залишаючись наодинці зі своїми думками.'
  },
  {
    id: 'romantic',
    colors: ['red', 'blue'],
    name: 'Романтик',
    description:
      'Всередині тебе живе естет, який любить дивитися на речі ширше та зазирати глибше за буденність. Ти цінуєш масштаб, простір та естетику давньої архітектури.'
  },
  {
    id: 'driver',
    colors: ['blue', 'yellow'],
    name: 'Драйвер',
    description:
      'Ніякого страху перед масштабними викликами — тільки готовність рухатися вперед. Твоя палітра — це високі швидкості, яскраві цілі та вміння самостійно задавати ритм життю.'
  },
  {
    id: 'wanderer',
    colors: ['red', 'coffee'],
    name: 'Блукач вуличками',
    description:
      'Твій день — це візуальна поезія, де кожен ритуал має значення. Увага до деталей та неспішний ритм прогулянок дарують відчуття справжнього комфорту й гармонії.'
  },
  {
    id: 'evening-soul',
    colors: ['yellow', 'coffee'],
    name: 'Душа вечора',
    description:
      'Ти закоханий у момент, коли місто запалює свої вогні. Моменти щирості й вечірнього тепла легко карбуються в пам’яті та ще довго зігрівають зсередини.'
  },
  {
    id: 'sun-lover',
    colors: ['red', 'yellow'],
    name: 'Любитель сонця',
    description:
      'Ти закоханий у момент, коли місто запалює свої вогні. Моменти щирості й вечірнього тепла легко карбуються в пам’яті та ще довго зігрівають зсередини.'
  }
];

const Bubble = ({ colorId, style, isSelected, onSelect }) => {
  const bubbleSrc = getImageFromS3(`images/soho-vibe/bubble-${colorId}.png`);

  return (
    <button
      type="button"
      aria-pressed={isSelected}
      className={`${styles.bubble} ${isSelected ? styles.bubbleSelected : ''}`}
      style={style}
      onClick={() => onSelect(colorId)}
    >
      <img src={bubbleSrc} alt="" />
      <span
        className={styles.bubbleOverlay}
        style={{
          maskImage: `url("${bubbleSrc}")`,
          WebkitMaskImage: `url("${bubbleSrc}")`
        }}
      >
        <CheckIcon className={styles.bubbleCheck} />
      </span>
    </button>
  );
};

const VibeScreen = ({ onComplete }) => {
  const [selectedColorIds, setSelectedColorIds] = useState([]);
  const [archetype, setArchetype] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPillVisible, setIsPillVisible] = useState(false);
  const resultRef = useRef(null);
  const containerRef = useRef(null);

  const isReady = selectedColorIds.length === MAX_SELECTED;

  const toggleColor = useCallback((colorId) => {
    setSelectedColorIds((current) => {
      if (current.includes(colorId)) {
        return current.filter((id) => id !== colorId);
      }
      if (current.length >= MAX_SELECTED) return current;
      return [...current, colorId];
    });
  }, []);

  const scrollToResult = useCallback(() => {
    window.requestAnimationFrame(() => {
      if (!resultRef.current) return;
      resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    if (!isReady) return;

    const nextArchetype = ARCHETYPES.find((item) =>
      selectedColorIds.every((colorId) => item.colors.includes(colorId))
    );
    if (!nextArchetype) return;

    setArchetype(nextArchetype);
    scrollToResult();
  }, [isReady, selectedColorIds, scrollToResult]);

  useEffect(() => {
    if (!archetype) return;
    scrollToResult();
  }, [archetype, scrollToResult]);

  const handleDownload = useCallback(async () => {
    if (!archetype || isDownloading) return;

    setIsDownloading(true);

    const imageUrl = getImageFromS3(
      `images/soho-vibe/${archetype.id}-full.png`
    );
    const fileName = `soho-vibe-${archetype.id}.png`;

    try {
      const response = await fetch(imageUrl);
      if (!response.ok) throw new Error(String(response.status));

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      window.open(imageUrl, '_blank');
    } finally {
      setIsDownloading(false);
    }
  }, [archetype, isDownloading]);

  const handleContinue = useCallback(() => {
    onComplete({ toReview: true });
  }, [onComplete]);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    const handleScroll = () => {
      setIsPillVisible(node.scrollTop > PILL_REVEAL_OFFSET);
    };

    handleScroll();
    node.addEventListener('scroll', handleScroll, { passive: true });

    return () => node.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div
      ref={containerRef}
      className={`${styles.container} ${
        archetype ? '' : styles.containerIntro
      }`}
      style={{
        backgroundImage: `url("${getImageFromS3('images/soho-vibe/bg.png')}")`
      }}
    >
      <div className={styles.hero}>
        <BrandMark className={styles.logo} />
        <div className={styles.intro}>
          <p className={styles.title}>Твій міський вайб</p>
          <p className={styles.lead}>
            Жити в одному місті — не означає бачити його однаково.
          </p>
          <p className={styles.subtitle}>
            Для тебе воно може бути про затишні дворики, а для когось — про
            блискучу річку, вздовж якої хочеться намотувати кілометри.
          </p>
        </div>
        <ScrollHint className={styles.scrollHint} />
        <p className={styles.prompt}>
          Обери 2 кольори, які тобі найближчі — дізнайся свій тип містянина та
          забирай приємний бонус!
        </p>
        <div className={styles.bubbles}>
          <Bubble
            colorId="blue"
            style={{
              left: '144px',
              top: '0px',
              width: '148px',
              height: '148px'
            }}
            isSelected={selectedColorIds.includes('blue')}
            onSelect={toggleColor}
          />
          <Bubble
            colorId="coffee"
            style={{
              left: '0px',
              top: '24px',
              width: '170px',
              height: '170px'
            }}
            isSelected={selectedColorIds.includes('coffee')}
            onSelect={toggleColor}
          />
          <Bubble
            colorId="green"
            style={{
              left: '0px',
              top: '163px',
              width: '135px',
              height: '135px'
            }}
            isSelected={selectedColorIds.includes('green')}
            onSelect={toggleColor}
          />
          <Bubble
            colorId="red"
            style={{
              left: '100px',
              top: '163px',
              width: '170px',
              height: '170px'
            }}
            isSelected={selectedColorIds.includes('red')}
            onSelect={toggleColor}
          />
          <Bubble
            colorId="yellow"
            style={{
              left: '210px',
              top: '106px',
              width: '135px',
              height: '135px'
            }}
            isSelected={selectedColorIds.includes('yellow')}
            onSelect={toggleColor}
          />
        </div>
        <button
          type="button"
          className={`${styles.glassButton} ${
            isReady ? '' : styles.glassButtonDisabled
          }`}
          onClick={handleConfirm}
        >
          <span className={styles.glassGleam} />
          <span className={styles.glassLabel}>Обрати</span>
        </button>
      </div>

      {archetype && (
        <div className={styles.result} ref={resultRef}>
          <div className={styles.card}>
            <div className={styles.cardContent}>
              <div className={styles.resultColors}>
                {archetype.colors.map((colorId, index) => (
                  <span
                    key={colorId}
                    className={styles.resultColor}
                    style={{
                      left: `${index * 34}px`,
                      backgroundImage: `linear-gradient(180deg, ${COLORS[colorId].from} 0%, ${COLORS[colorId].to} 100%)`
                    }}
                  />
                ))}
              </div>
              <p className={styles.kicker}>Твій міський архетип</p>
              <p className={styles.archetypeName}>{archetype.name}</p>
              <div className={styles.archetypeText}>
                <p>{archetype.description}</p>
                <p>{WALLPAPER_HINT}</p>
              </div>
              <div
                className={styles.wallpaper}
                style={{
                  backgroundImage: `url("${getImageFromS3(
                    `images/soho-vibe/${archetype.id}.png`
                  )}")`
                }}
              />
              <button
                type="button"
                className={`${styles.glassButton} ${
                  isDownloading ? styles.glassButtonDisabled : ''
                }`}
                onClick={handleDownload}
              >
                <span className={styles.glassGleam} />
                <span className={styles.glassLabel}>
                  Завантажити
                  <DownloadIcon className={styles.downloadIcon} />
                </span>
              </button>
            </div>
          </div>
          <div className={styles.closing}>
            <p className={styles.closingLead}>
              Сподіваємося, ми вгадали твій настрій!
            </p>
            <p className={styles.closingText}>
              Бажаємо тобі якомога більше яскравих моментів та натхненних
              прогулянок.
            </p>
          </div>
        </div>
      )}

      <button
        className={`${styles.reviewPill} ${
          isPillVisible ? styles.reviewPillVisible : ''
        }`}
        type="button"
        onClick={handleContinue}
      >
        <span className={styles.reviewLabel}>Залишити відгук</span>
        <ReviewChevronIcon className={styles.reviewChevron} />
      </button>
    </div>
  );
};

export default VibeScreen;
